import React, { useEffect, useMemo, useState } from 'react';
import { onValue, ref, update } from 'firebase/database';
import { database } from '../firebase';

type MessageItem = {
  Nombre?: string;
  Email?: string;
  Asunto?: string;
  Mensaje?: string;
  ProjectID?: string;
  Prioridad?: boolean | string;
};

function MessagesSection() {
  const [messages, setMessages] = useState<Record<string, MessageItem>>({});
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [status, setStatus] = useState('');
  const [isResolving, setIsResolving] = useState(false);

  useEffect(() => {
    const messagesRef = ref(database, 'Messages');
    const unsubscribe = onValue(
      messagesRef,
      (snapshot) => {
        const value = snapshot.val() as Record<string, MessageItem> | null;
        if (!value) {
          setMessages({});
          setSelectedId(null);
          return;
        }
        setMessages(value);
        if (selectedId && !value[selectedId]) {
          setSelectedId(null);
        }
      },
      () => {
        setStatus('Error al cargar mensajes.');
      }
    );

    return () => unsubscribe();
  }, [selectedId]);

  const hasMessages = useMemo(() => Object.keys(messages).length > 0, [messages]);

  const sortedMessages = useMemo(() => {
    return Object.entries(messages).sort(([, a], [, b]) => {
      const aPriority = a.Prioridad === true || a.Prioridad === 'true';
      const bPriority = b.Prioridad === true || b.Prioridad === 'true';
      if (aPriority === bPriority) {
        return 0;
      }
      return aPriority ? -1 : 1;
    });
  }, [messages]);

  const selectedMessage = selectedId ? messages[selectedId] : null;

  const handleCopyProjectId = async () => {
    if (!selectedMessage?.ProjectID) {
      return;
    }
    try {
      await navigator.clipboard.writeText(selectedMessage.ProjectID);
      setStatus('ProjectID copiado.');
      setTimeout(() => setStatus(''), 2000);
    } catch (copyError) {
      setStatus('No se pudo copiar el ProjectID.');
    }
  };

  const handleResolve = async () => {
    if (!selectedId || !selectedMessage) {
      return;
    }
    setIsResolving(true);
    try {
      const updates: Record<string, MessageItem | null> = {};
      updates[`Resolved/${selectedId}`] = selectedMessage;
      updates[`Messages/${selectedId}`] = null;
      await update(ref(database), updates);
      setStatus('Mensaje resuelto.');
      setSelectedId(null);
    } catch (resolveError) {
      setStatus('No se pudo resolver el mensaje.');
    } finally {
      setIsResolving(false);
    }
  };

  const isPriority =
    selectedMessage?.Prioridad === true || selectedMessage?.Prioridad === 'true';

  return (
    <div className="section">
      <div className="section-header">
        <h2>Mensajes de contacto</h2>
      </div>
      {!hasMessages ? (
        <>
          <div className="hint">No hay mensajes nuevos.</div>
          {status ? <div className="inline-toast inline-toast-neutral">{status}</div> : null}
        </>
      ) : selectedMessage ? (
        <div className="message-detail">
          <div className="message-detail-header">
            <button className="secondary-button" type="button" onClick={() => setSelectedId(null)}>
              Volver
            </button>
            <button
              className="primary-button"
              type="button"
              onClick={handleResolve}
              disabled={isResolving}
            >
              {isResolving ? 'Resolviendo...' : 'Resolver'}
            </button>
          </div>
          {status ? <div className="inline-toast inline-toast-neutral">{status}</div> : null}
          <div className="message-detail-card">
            <div className="message-detail-title">
              <div>
                <div className="message-label">Asunto</div>
                <div className="message-subject">{selectedMessage.Asunto || '-'}</div>
              </div>
              {isPriority ? <div className="priority-pill">Resolver urgente</div> : null}
            </div>
            <div className="message-grid">
              <div>
                <div className="message-label">Nombre</div>
                <div>{selectedMessage.Nombre || '-'}</div>
              </div>
              <div>
                <div className="message-label">Email</div>
                <div>{selectedMessage.Email || '-'}</div>
              </div>
              <div>
                <div className="message-label">ProjectID</div>
                <button className="copy-button" type="button" onClick={handleCopyProjectId}>
                  {selectedMessage.ProjectID || '-'}
                </button>
              </div>
            </div>
            <div className="message-body">
              <div className="message-label">Mensaje</div>
              <p>{selectedMessage.Mensaje || '-'}</p>
            </div>
          </div>
        </div>
      ) : (
        <div className="message-list">
          {sortedMessages.map(([messageId, message]) => {
            const isItemPriority =
              message.Prioridad === true || message.Prioridad === 'true';
            return (
              <div
                className={`message-card ${isItemPriority ? 'message-card-priority' : ''}`}
                key={messageId}
              >
              <div>
                <div className="message-subject">
                  {message.Asunto || 'Sin asunto'}
                </div>
                <div className="message-meta">
                  <span>{message.Nombre || 'Sin nombre'}</span>
                  <span>{message.Email || 'Sin email'}</span>
                </div>
              </div>
              {isItemPriority ? (
                <div className="priority-pill priority-pill-inline">Resolver urgente</div>
              ) : null}
              <button
                className="ghost-button"
                type="button"
                onClick={() => setSelectedId(messageId)}
              >
                Ver detalles
              </button>
            </div>
          );
          })}
        </div>
      )}
      {hasMessages && !selectedMessage && status ? (
        <div className="inline-toast inline-toast-neutral">{status}</div>
      ) : null}
    </div>
  );
}

export default MessagesSection;
